import {
  Box,
  Button,
  Flex,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';
import {
  InfoCircle,
  RightIcon,
  useDashboardStore,
} from '@devd-client/devd/components';
import { useInsightCardDataGql } from '../../graphql';
import { Link } from 'react-router-dom';
import type { InsightCardDataItem } from '../../helpers/home.types';

const TopReposCard = () => {
  const { colors } = useTheme();
  const { selected, selectedTeam } = useDashboardStore();

  const { data: repos, isFetching: reposLoading } = useInsightCardDataGql(
    'repos',
    selectedTeam.value,
    selected.startDate,
    selected.endDate,
    selected?.sprintId
  );

  if (reposLoading) {
    return (
      <Box position="relative">
        <Skeleton height="220px" minWidth="500px" />
        <Spinner position="absolute" top="45%" left="45%" />
      </Box>
    );
  }

  const items = repos?.data?.slice(0, 5) ?? [];

  return (
    <Flex
      p={4}
      border="1px solid #E0E0E0"
      borderRadius="8px"
      boxShadow="0px 1px 2px 0px rgba(16, 24, 40, 0.05)"
      direction="column"
      minHeight="220px"
      justify="space-between"
      _hover={{
        boxShadow: 'lg',
        transition: '0.2s all',
      }}
    >
      <Flex mb={4} align="center">
        <Text color="text.primary" fontWeight="semibold">
          {repos?.title || 'Top Repositories'}
        </Text>
        {repos?.subTitle && (
          <Tooltip
            hasArrow
            aria-label="top-repos"
            label={repos.subTitle}
            placement="top"
          >
            <Box ml={1} cursor="default">
              <InfoCircle />
            </Box>
          </Tooltip>
        )}
      </Flex>

      <Flex direction="column" gap={3} flex={1}>
        {items.length > 0 ? (
          items.map((item: InsightCardDataItem, idx: number) => (
            <Flex key={idx} align="center" justify="space-between">
              <Flex align="center" gap={2} minW={0} flex={1}>
                <Text
                  fontFamily="heading"
                  fontSize="xs"
                  fontWeight="medium"
                  color="text.secondary"
                  w="16px"
                  flexShrink={0}
                >
                  {idx + 1}.
                </Text>
                {item?.link ? (
                  <Tooltip
                    aria-label="tooltip"
                    hasArrow
                    placement="top"
                    label={item?.subTitle}
                  >
                    <Text
                      as={Link}
                      to={item.link}
                      target="_blank"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      fontFamily="heading"
                      fontSize="sm"
                      fontWeight="medium"
                      color="text.secondary2"
                      isTruncated
                      _hover={{ color: 'primary', textDecoration: 'underline' }}
                    >
                      {item?.title}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text
                    fontFamily="heading"
                    fontSize="sm"
                    fontWeight="medium"
                    color="text.secondary2"
                    isTruncated
                  >
                    {item?.title}
                  </Text>
                )}
              </Flex>
              <Text
                fontFamily="heading"
                fontSize="sm"
                fontWeight="semibold"
                color="text.primary"
                ml={3}
                flexShrink={0}
              >
                {item?.count ?? '-'}
              </Text>
            </Flex>
          ))
        ) : (
          <Flex align="center" justify="center" flex={1}>
            <Text fontFamily="heading" fontSize="sm" color="text.secondary">
              No repository data available.
            </Text>
          </Flex>
        )}
      </Flex>

      {repos?.link && (
        <Box mt={4}>
          <Button
            variant="unstyled"
            color="text.secondary2"
            fontWeight="normal"
            size="sm"
            fontSize="sm"
            _hover={{ textDecoration: 'underline' }}
            rightIcon={
              <RightIcon
                height="11px"
                width="11px"
                color={colors.text.secondary2}
              />
            }
          >
            <Link
              to={`${repos.link}${
                repos.link.includes('?')
                  ? `&${window.location.href.split('?')[1]}`
                  : `?${window.location.href.split('?')[1]}`
              }`}
            >
              View Details
            </Link>
          </Button>
        </Box>
      )}
    </Flex>
  );
};

export default TopReposCard;
